import { useEffect, useRef, useState } from "react"
import { api } from "../../../../lib/api"
import type { ApiError } from "../../../../lib/api"
import { useSelectedAccount } from "../../../../contexts/AccountContext"
import { useAudioSearch } from "../../../../hooks/useAudioSearch"
import { useAudioPlayer } from "../../../../hooks/useAudioPlayer"
import type { Audio, ScheduledPostAudio } from "../../../../types/scheduled-post"
import { formatAudioDuration } from "../../../../lib/audioDuration"
import AudioPickerModal from "./AudioPickerModal"

const AUDIO_DELETED_MESSAGE =
  "この音源は Instagram で削除されています。選び直すか削除してください"

// 組織 ID が未解決の間だけモーダルを開く導線を無効化する（下の ORG_UNRESOLVED 参照）。
// 解決は通常一瞬なので、バナーや文言は出さず tooltip だけで補う
const ORG_LOADING_MESSAGE = "組織情報を読み込み中です"
// 組織一覧は取得済みなのに該当アカウントが見つからない状態。ロード中と違って待っても解決
// しないため、待たせる文言を出してはいけない（下の orgUnresolvedReason のコメントを参照）。
const ORG_UNRESOLVABLE_MESSAGE = "組織情報を取得できませんでした。ページを再読み込みしてください"

type Props = {
  selectedAccountId: number
  value: ScheduledPostAudio | null
  onChange: (audio: ScheduledPostAudio | null) => void
}

// リール音源（BGM）セクション。未選択時は「音源を選ぶ」ボタン、選択後はカード＋音量スライダー 2 本。
// 送信対象の値（ScheduledPostAudio）は usePostForm が持ち、ここは value + onChange の
// 制御コンポーネント（CategorySelector と同形）。
//
// アートワーク・試聴 URL は約1.5日で失効するため保存されておらず、`audios#show` で都度取り直す
// （この再取得が音源の失効検知も兼ねる。決定 #13 / #19）。
const AudioSection = ({ selectedAccountId, value, onChange }: Props) => {
  const { organizations } = useSelectedAccount()
  const orgId = organizations.find(o => o.accounts.some(a => a.id === selectedAccountId))?.id

  const [modalOpen, setModalOpen] = useState(false)
  // 表示用メタ（アートワーク・試聴 URL）。保存対象ではないので value とは別に持つ。
  // 音源を差し替えたときに前の音源のメタを出さないよう、value と audio_id が一致するときだけ使う
  // （effect 内で state をリセットしない = cascading render を避ける）
  const [fetchedMeta, setFetchedMeta]   = useState<Audio | null>(null)
  // 「削除済み」の判定は組織 × 音源の組でだけ有効にする。組織が切り替われば（= 別の組織として
  // 問い合わせ直せるようになれば）警告は消えて再取得が走る。一度立ったら戻らない構造にしない
  const [deletedKey, setDeletedKey] = useState<string | null>(null)

  const meta    = fetchedMeta && fetchedMeta.audio_id === value?.audio_id ? fetchedMeta : null
  const deleted = !!value && deletedKey === `${orgId}:${value.audio_id}`

  const search = useAudioSearch(selectedAccountId, orgId)
  const player = useAudioPlayer()
  const { stop: stopPlayer } = player

  // 組織が未解決（`organizations` のロード中）のうちはモーダルを開かせない。
  // モーダルを開くと `audios#index`（トレンド取得。以降は検索も）が走るが、`organization_id`
  // なしで投げるとサーバー側の `set_organization` が `current_user.organizations.first` に
  // フォールバックするため、複数組織のユーザーでは別組織のアカウントを見て `set_account` が
  // 404 を返す。UI には「音源の取得に失敗しました」としか出ず理由が分からないので、
  // そもそも呼ばせない（下の `audios#show` のガードと同じ理由・同じ組織 ID の経路）。
  // 「一瞬押せないだけ」に見えても、外すと上記の誤エラーが復活する
  const orgUnresolved = orgId == null
  // orgId が null になる理由は 2 通りあり、ユーザーに伝えるべきことが正反対になる。
  //
  // 1. `organizations` がまだ空 = ロード中。待てば解決する
  // 2. `organizations` は取得済みだが該当アカウントが無い。待っても解決しない
  //
  // 2 が起きるのは、`AccountContext` が `loadOrganizations().catch(() => {})` で失敗を
  // 握り潰して再試行しないため、取得に失敗すると `organizations` が空のままになるケースと、
  // 編集中の投稿のアカウントが組織から外された／連携解除されたケース。新規作成画面は
  // 別のガードでフォームごと描画されないが、編集画面は `accountId` が編集対象の投稿から
  // 直接決まるので、フォームが正常に描画されたうえでここだけが永久に解決しない。
  // 一律で「読み込み中です」と出すと、リロードすれば直ることに気付けず行き止まりになる。
  const orgUnresolvedReason = !orgUnresolved
    ? undefined
    : organizations.length === 0 ? ORG_LOADING_MESSAGE : ORG_UNRESOLVABLE_MESSAGE

  // 編集画面での復元時（value はあるがメタがない）にアートワーク・試聴 URL を取り直す。
  // モーダルから選んだ直後はメタが揃っているため追加コールは発生しない。
  const audioId = value?.audio_id
  const hasMeta = meta != null
  useEffect(() => {
    if (!audioId || hasMeta || deleted) return
    // 組織が未解決（`organizations` のロード中）のうちは問い合わせない。
    // `organization_id` なしで投げると `set_account` が `.first` の組織にフォールバックし、
    // 複数組織のユーザーでは別組織のアカウントを見て 404 になる。`audios#show` の 404 は
    // 「音源が Instagram にない」と区別できないため、正常な音源に削除警告が出てしまう
    if (orgId == null) return
    // `audios#show` のルートは :id が数字のみ。数字以外だとルーティングの 404 を
    // 「音源が削除された」と誤表示するため、そもそも問い合わせない
    if (!/^\d+$/.test(audioId)) return

    let aborted = false
    api.get<{ audio: Audio }>(
      `/instagram_accounts/${selectedAccountId}/audios/${audioId}?organization_id=${orgId}`
    )
      .then(data => { if (!aborted) setFetchedMeta(data.audio) })
      // 404 はステータスだけでは判断しない。`audios#show` は「音源が Instagram にない」
      // （`audio_not_found`）と `set_account` の「アカウントが見つかりません」の 2 経路で
      // 404 を返すため、後者を削除と誤断定すると取り消せない警告が出る。上のガードで
      // 後者はほぼ起きないが、ガードが外れても誤判定しないよう本文まで確認する
      .catch((e: ApiError) => {
        if (!aborted && e?.status === 404 && e?.data?.error === "audio_not_found") {
          setDeletedKey(`${orgId}:${audioId}`)
        }
      })
    return () => { aborted = true }
  }, [audioId, hasMeta, deleted, selectedAccountId, orgId])

  // アカウントを切り替えたら選択済み音源を破棄する。可用性はアカウント単位なので、
  // 別アカウントで選んだ音源を積んだまま公開すると failed（＋失敗メール）になる
  const valueRef = useRef(value)
  useEffect(() => { valueRef.current = value }, [value])

  const prevAccountIdRef = useRef(selectedAccountId)
  useEffect(() => {
    if (prevAccountIdRef.current === selectedAccountId) return
    prevAccountIdRef.current = selectedAccountId

    stopPlayer()
    setFetchedMeta(null)
    setDeletedKey(null)
    // 元から未選択なら onChange を呼ばない（「音源に触った」扱いにして remove_audio を
    // 送らせないため）
    if (valueRef.current) onChange(null)
  }, [selectedAccountId, onChange, stopPlayer])

  const handleSelect = (audio: Audio) => {
    setFetchedMeta(audio)
    setDeletedKey(null)
    player.stop()
    onChange({
      audio_id:       audio.audio_id,
      title:          audio.title,
      artist:         audio.artist,
      audio_type:     audio.audio_type,
      duration_in_ms: audio.duration_in_ms,
      // 差し替えでも音量は既定の 100 に戻す（子レコードの destroy ＋ create と同じ扱い）
      audio_volume:   100,
      video_volume:   100,
    })
    setModalOpen(false)
  }

  const handleRemove = () => {
    player.stop()
    setFetchedMeta(null)
    setDeletedKey(null)
    onChange(null)
  }

  const duration = formatAudioDuration(value?.duration_in_ms ?? null)
  const artist   = value?.audio_type === "original_sound" && value.artist ? `@${value.artist}` : value?.artist
  const isPlaying = !!value && player.playingId === value.audio_id

  return (
    <section className="post-create-form__section">
      <div className="audio-section__header">
        <h2 className="post-create-form__section-title">音源（BGM）</h2>
        <span className="audio-section__optional">任意</span>
      </div>

      {!value ? (
        <button
          type="button"
          className="audio-section__picker-btn"
          onClick={() => setModalOpen(true)}
          disabled={orgUnresolved}
          title={orgUnresolvedReason}
        >
          <i className="bi bi-music-note-beamed" />
          <span className="audio-section__picker-label">音源を選ぶ</span>
          <span className="audio-section__picker-hint">リールにInstagramの楽曲を追加できます</span>
        </button>
      ) : (
        <>
          {deleted && (
            <div className="media-errors__item">
              <i className="bi bi-exclamation-circle" />{AUDIO_DELETED_MESSAGE}
            </div>
          )}

          <div className="audio-card">
            <div className="audio-artwork">
              {meta?.cover_artwork_url ? (
                <img className="audio-artwork__img" src={meta.cover_artwork_url} alt="" />
              ) : (
                <i className="bi bi-music-note-beamed" />
              )}
            </div>

            <div className="audio-card__meta">
              <div className="audio-card__title">{value.title || "タイトルなし"}</div>
              <div className="audio-card__sub">
                {artist && <span>{artist}</span>}
                {duration && <span>{duration}</span>}
              </div>
            </div>

            {/* モーダルの行と違い、カードは 1 行しか出ず横幅に余裕があるので、
                外部リンクはアイコンだけにせずラベルも見せる（モーダル側は 44px 制約で
                アイコンのみ。アイコンと文言は両者で揃えてある） */}
            {meta?.preview_url ? (
              <button
                type="button"
                className="audio-preview-btn"
                onClick={() => player.toggle(value.audio_id, meta.preview_url)}
                aria-label={isPlaying ? "試聴を停止" : "試聴する"}
              >
                <i className={isPlaying ? "bi bi-pause-fill" : "bi bi-play-fill"} />
              </button>
            ) : meta?.instagram_url ? (
              <a
                className="audio-card__external"
                href={meta.instagram_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="bi bi-instagram" />
                <span>Instagram で聴く</span>
              </a>
            ) : (
              <span className="audio-card__no-preview">試聴不可</span>
            )}

            <div className="audio-card__actions">
              <button
                type="button"
                className="ps-btn"
                onClick={() => setModalOpen(true)}
                disabled={orgUnresolved}
                title={orgUnresolvedReason}
              >
                変更
              </button>
              <button type="button" className="ps-btn ps-btn--danger" onClick={handleRemove}>削除</button>
            </div>
          </div>

          <div className="audio-volumes">
            <div className="audio-volume">
              <label className="form-group__label" htmlFor="audio-volume">
                音源の音量 <span className="audio-volume__value">{value.audio_volume}</span>
              </label>
              <input
                id="audio-volume"
                className="audio-volume__range"
                type="range"
                min={0}
                max={100}
                value={value.audio_volume}
                onChange={e => onChange({ ...value, audio_volume: Number(e.target.value) })}
              />
            </div>
            <div className="audio-volume">
              <label className="form-group__label" htmlFor="video-volume">
                動画の音量 <span className="audio-volume__value">{value.video_volume}</span>
              </label>
              <input
                id="video-volume"
                className="audio-volume__range"
                type="range"
                min={0}
                max={100}
                value={value.video_volume}
                onChange={e => onChange({ ...value, video_volume: Number(e.target.value) })}
              />
            </div>
          </div>
        </>
      )}

      {modalOpen && (
        <AudioPickerModal
          search={search}
          player={player}
          onSelect={handleSelect}
          onClose={() => setModalOpen(false)}
        />
      )}
    </section>
  )
}

export default AudioSection
